import * as THREE from 'three';

export type SectionAxis = 'x' | 'y' | 'z';

/** Compute the visible mesh/plane intersection segments used to draw the section boundary. */
export function computeSectionIntersectionSegments(
  assembly: THREE.Object3D,
  plane: THREE.Plane,
  isGroupVisible?: (groupId: number) => boolean,
): Float32Array {
  assembly.updateMatrixWorld(true);
  const segments: number[] = [];
  const normal = plane.normal;
  const epsilon = 1e-6;
  const nudge = 0.02;
  const nx = normal.x * nudge, ny = normal.y * nudge, nz = normal.z * nudge;
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  const p1 = new THREE.Vector3(), p2 = new THREE.Vector3();

  assembly.traverse((child) => {
    const groupId = child.userData?.groupId;
    if (typeof groupId === 'number' && isGroupVisible && !isGroupVisible(groupId)) return;
    if (!(child instanceof THREE.Mesh) || !child.visible || !child.geometry || child.userData?.isSupport) return;
    const geometry = child.geometry;
    const position = geometry.attributes.position;
    const index = geometry.index;
    if (!position) return;
    const triangleCount = index ? index.count / 3 : position.count / 3;

    for (let i = 0; i < triangleCount; i++) {
      let ia = i * 3, ib = ia + 1, ic = ia + 2;
      if (index) {
        ia = index.getX(ia);
        ib = index.getX(ib);
        ic = index.getX(ic);
      }
      a.fromBufferAttribute(position, ia).applyMatrix4(child.matrixWorld);
      b.fromBufferAttribute(position, ib).applyMatrix4(child.matrixWorld);
      c.fromBufferAttribute(position, ic).applyMatrix4(child.matrixWorld);
      let da = a.dot(normal) + plane.constant;
      let db = b.dot(normal) + plane.constant;
      let dc = c.dot(normal) + plane.constant;
      if (Math.abs(da) < epsilon) da = 0;
      if (Math.abs(db) < epsilon) db = 0;
      if (Math.abs(dc) < epsilon) dc = 0;
      const za = da === 0, zb = db === 0, zc = dc === 0;
      const zeros = Number(za) + Number(zb) + Number(zc);
      let hasSegment = false;

      if (zeros === 3) continue;
      if (zeros === 2) {
        if (za && zb && dc > 0) { p1.copy(a); p2.copy(b); hasSegment = true; }
        else if (zb && zc && da > 0) { p1.copy(b); p2.copy(c); hasSegment = true; }
        else if (zc && za && db > 0) { p1.copy(c); p2.copy(a); hasSegment = true; }
      } else if (zeros === 1) {
        if (za && ((db > 0 && dc < 0) || (db < 0 && dc > 0))) {
          p1.copy(a); p2.copy(b).lerp(c, db / (db - dc)); hasSegment = true;
        } else if (zb && ((da > 0 && dc < 0) || (da < 0 && dc > 0))) {
          p1.copy(b); p2.copy(a).lerp(c, da / (da - dc)); hasSegment = true;
        } else if (zc && ((da > 0 && db < 0) || (da < 0 && db > 0))) {
          p1.copy(c); p2.copy(a).lerp(b, da / (da - db)); hasSegment = true;
        }
      } else {
        const intersections: THREE.Vector3[] = [];
        if ((da > 0 && db < 0) || (da < 0 && db > 0)) intersections.push(a.clone().lerp(b, da / (da - db)));
        if ((db > 0 && dc < 0) || (db < 0 && dc > 0)) intersections.push(b.clone().lerp(c, db / (db - dc)));
        if ((dc > 0 && da < 0) || (dc < 0 && da > 0)) intersections.push(c.clone().lerp(a, dc / (dc - da)));
        if (intersections.length === 2) { p1.copy(intersections[0]!); p2.copy(intersections[1]!); hasSegment = true; }
      }

      if (hasSegment && p1.distanceToSquared(p2) > 1e-8) {
        segments.push(p1.x + nx, p1.y + ny, p1.z + nz, p2.x + nx, p2.y + ny, p2.z + nz);
      }
    }
  });
  return new Float32Array(segments);
}

/** Build the bold, gap-free section border geometry from the computed intersection segments. */
export function buildSectionCutOutline(
  positions: Float32Array,
  planeNormal: THREE.Vector3,
  lineMaterial: THREE.LineBasicMaterial,
  ribbonMaterial: THREE.MeshBasicMaterial,
): THREE.Group | null {
  if (positions.length === 0) return null;
  const group = new THREE.Group();
  group.renderOrder = 999;
  const halfWidth = 0.16;
  const totalSegments = positions.length / 6;
  const keyOf = (p: THREE.Vector3) => `${Math.round(p.x * 200)}_${Math.round(p.y * 200)}_${Math.round(p.z * 200)}`;
  const adjacency = new Map<string, Array<{ edgeIdx: number }>>();
  const segments: Array<[THREE.Vector3, THREE.Vector3]> = [];

  for (let i = 0; i < totalSegments; i++) {
    const offset = i * 6;
    const a = new THREE.Vector3(positions[offset]!, positions[offset + 1]!, positions[offset + 2]!);
    const b = new THREE.Vector3(positions[offset + 3]!, positions[offset + 4]!, positions[offset + 5]!);
    segments.push([a, b]);
    const keyA = keyOf(a), keyB = keyOf(b);
    if (!adjacency.has(keyA)) adjacency.set(keyA, []);
    if (!adjacency.has(keyB)) adjacency.set(keyB, []);
    adjacency.get(keyA)!.push({ edgeIdx: i });
    adjacency.get(keyB)!.push({ edgeIdx: i });
  }

  const visited = new Uint8Array(totalSegments);
  const chains: THREE.Vector3[][] = [];
  for (let i = 0; i < totalSegments; i++) {
    if (visited[i]) continue;
    const chain: THREE.Vector3[] = [];
    let current = segments[i]![0].clone();
    chain.push(current);
    let edge: number | null = i;
    while (edge !== null) {
      visited[edge] = 1;
      const [a, b] = segments[edge]!;
      current = (a.distanceToSquared(current) < 1e-6 ? b : a).clone();
      chain.push(current);
      const next = adjacency.get(keyOf(current))?.find((candidate) => !visited[candidate.edgeIdx]);
      edge = next ? next.edgeIdx : null;
    }
    if (chain.length >= 2) chains.push(chain);
  }

  const ribbonPositions: number[] = [];
  for (const chain of chains) {
    if (chain.length < 2) continue;
    const closed = chain[0]!.distanceTo(chain[chain.length - 1]!) < 0.01;
    const count = closed ? chain.length - 1 : chain.length;
    if (count < 2) continue;
    const left: THREE.Vector3[] = [];
    const right: THREE.Vector3[] = [];
    for (let i = 0; i < count; i++) {
      const current = chain[i]!;
      let tangentIn: THREE.Vector3, tangentOut: THREE.Vector3;
      if (closed) {
        tangentIn = new THREE.Vector3().subVectors(current, chain[(i - 1 + count) % count]!).normalize();
        tangentOut = new THREE.Vector3().subVectors(chain[(i + 1) % count]!, current).normalize();
      } else if (i === 0) {
        tangentOut = new THREE.Vector3().subVectors(chain[1]!, current).normalize();
        tangentIn = tangentOut.clone();
      } else if (i === count - 1) {
        tangentIn = new THREE.Vector3().subVectors(current, chain[count - 2]!).normalize();
        tangentOut = tangentIn.clone();
      } else {
        tangentIn = new THREE.Vector3().subVectors(current, chain[i - 1]!).normalize();
        tangentOut = new THREE.Vector3().subVectors(chain[i + 1]!, current).normalize();
      }
      const uIn = new THREE.Vector3().crossVectors(planeNormal, tangentIn).normalize();
      const uOut = new THREE.Vector3().crossVectors(planeNormal, tangentOut).normalize();
      const miter = new THREE.Vector3().addVectors(uIn, uOut);
      let scale = halfWidth;
      if (miter.lengthSq() > 1e-4) {
        miter.normalize();
        scale = halfWidth / Math.max(miter.dot(uIn), 0.35);
      } else miter.copy(uIn);
      miter.multiplyScalar(Math.min(scale, halfWidth * 2.5));
      left.push(new THREE.Vector3().addVectors(current, miter));
      right.push(new THREE.Vector3().subVectors(current, miter));

      if (uIn.dot(uOut) < 0.96) {
        const steps = 8;
        for (let step = 0; step < steps; step++) {
          const a1 = (step / steps) * Math.PI * 2;
          const a2 = ((step + 1) / steps) * Math.PI * 2;
          const pA = current.clone().addScaledVector(uIn, Math.cos(a1) * halfWidth).addScaledVector(tangentIn, Math.sin(a1) * halfWidth);
          const pB = current.clone().addScaledVector(uIn, Math.cos(a2) * halfWidth).addScaledVector(tangentIn, Math.sin(a2) * halfWidth);
          ribbonPositions.push(current.x, current.y, current.z, pA.x, pA.y, pA.z, pB.x, pB.y, pB.z);
        }
      }
    }
    const segmentCount = closed ? count : count - 1;
    for (let i = 0; i < segmentCount; i++) {
      const next = (i + 1) % count;
      const l1 = left[i]!, r1 = right[i]!, l2 = left[next]!, r2 = right[next]!;
      ribbonPositions.push(l1.x,l1.y,l1.z,r1.x,r1.y,r1.z,r2.x,r2.y,r2.z,
        l1.x,l1.y,l1.z,r2.x,r2.y,r2.z,l2.x,l2.y,l2.z);
    }
  }

  if (ribbonPositions.length > 0) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(ribbonPositions, 3));
    const ribbon = new THREE.Mesh(geometry, ribbonMaterial);
    ribbon.renderOrder = 999;
    group.add(ribbon);
  }
  const lineGeometry = new THREE.BufferGeometry();
  lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const line = new THREE.LineSegments(lineGeometry, lineMaterial);
  line.renderOrder = 1000;
  group.add(line);
  return group;
}

export function sectionAxisIndex(axis: SectionAxis): 0 | 1 | 2 {
  return axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
}

/** Position a section plane across a model's bounds, leaving a tiny safe inset at either end. */
export function sectionPlaneCoordinate(box: THREE.Box3, axis: SectionAxis, percent: number): number {
  if (box.isEmpty()) return 0;
  const axisIndex = sectionAxisIndex(axis);
  const min = box.min.getComponent(axisIndex);
  const max = box.max.getComponent(axisIndex);
  const span = max - min;
  const inset = Math.max(span * 0.002, 0.05);
  return (min - inset) + (span + 2 * inset) * (percent / 100);
}

/** Returns the camera's signed side relative to the model center on the selected axis. */
export function cameraSideOnSectionAxis(
  camera: THREE.Camera,
  box: THREE.Box3,
  axis: SectionAxis,
): number {
  if (box.isEmpty()) return 0;
  const axisIndex = sectionAxisIndex(axis);
  const center = new THREE.Vector3();
  box.getCenter(center);
  const size = new THREE.Vector3();
  box.getSize(size);
  const extent = Math.max(size.getComponent(axisIndex), 1e-4);
  const distance = camera.position.getComponent(axisIndex) - center.getComponent(axisIndex);
  if (Math.abs(distance) < extent * 0.02) return 0;
  return Math.sign(distance);
}
